/**
 * Служебное сообщение — стеклянной панелью поверх экрана, а не вместо него.
 *
 * До этого обрыв связи заменял всю страницу одной серой строкой: владелец
 * читал матрицу гипервизоров, сеть моргнула — и на экране пусто, а вернуться
 * туда, где был, можно только вспомнив, где был. Между тем ничего не пропало:
 * данные на экране остались верными на момент обрыва, и честнее показать их с
 * оговоркой, чем стереть.
 *
 * Отсюда устройство: панель НЕ перехватывает нажатия (`pointer-events: none` у
 * подложки) и не затемняет фон. Она сообщает, а не требует. Если однажды
 * понадобится сообщение, требующее ответа, — это будет другой компонент, с
 * подложкой и фокусом, и путать их не надо.
 *
 * Стекло здесь не украшение: полупрозрачность с размытием оставляет видимым
 * то, что под ней, — то есть сам экран, к которому относится сообщение.
 * Непрозрачная плашка того же размера прятала бы ровно тот кусок, из-за
 * которого её и читают.
 */
import type { ReactNode } from 'react';

export type NoticeTone = 'working' | 'warn' | 'bad';

export function Notice({ tone, title, detail, footer }: {
  tone: NoticeTone;
  title: string;
  /** Одна фраза о том, что это значит для того, кто смотрит. */
  detail?: string;
  /** Что-то живое: обратный отсчёт, счётчик попыток. */
  footer?: ReactNode;
}): React.JSX.Element {
  return (
    <div className="glass__layer" role="status" aria-live="polite">
      <div className={`glass glass--${tone}`}>
        <div className="glass__sheen" aria-hidden="true" />
        <div className="glass__body">
          <span className={`glass__pulse glass__pulse--${tone}`} aria-hidden="true" />
          <div>
            <p className="glass__title">{title}</p>
            {detail && <p className="glass__detail">{detail}</p>}
            {footer && <p className="glass__footer">{footer}</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
